import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import xor from 'lodash/xor';
import { t } from 'ttag';

import { SeoHubBlockTypeEnum } from '@v5Models';
import { ActiveFilters, useFilterData, useIndexedFilters, useWithIndexedFilters } from '@hook/productFilters';
import { sendFilterApplyEvent, sendFilterBrandChoiceEvent } from '@utils/analytics';
import { setBrandPageReferrerToStorage, useCatalogRouteMatch, useCollectionRouteMatch } from '@utils/common';
import { BRAND_PAGE_REFERRER } from '@constants';
import { CatalogThematicFilters, Filter } from '@components/CatalogThematicFilters';

import s from './SeoHub.sss';

type BaseFilter = {
  title: string,
  image: string,
};

type CharacteristicFilter = BaseFilter & {
  characteristicChoice: {
    id: number,
    isIndexed: boolean,
  }
};

type ColorFilter = BaseFilter & {
  color: {
    id: string,
    isIndexed: boolean,
  }
};

type BrandFilter = BaseFilter & {
  brand: {
    id: number,
    isIndexed: boolean,
    name: string
  }
};

export type SeoHubItems = {
  title: string;
  url: string;
  pictureUrl?: string | null;
};

interface Filters {
  characteristics: CharacteristicFilter[],
  colors: ColorFilter[],
  brands: BrandFilter[],
}

export type SeoHubProps = {
  className?: string,
  filters?: Filters,
  seoHubItems?: Partial<Record<SeoHubBlockTypeEnum, SeoHubItems[]>>,
};

export const SeoHub: React.FC<SeoHubProps> = ({
  className,
  filters,
  seoHubItems,
}) => {
  const history = useHistory();
  const catalogMatch = useCatalogRouteMatch();
  const collectionMatch = useCollectionRouteMatch();
  const {
    filters: activeFilters,
    setFilter: setActiveFilter,
  } = useFilterData();
  const withIndexedFilters = useWithIndexedFilters();
  const { generateIndexedFilterPath } = useIndexedFilters();

  const getFilterUrl = (filterKey: keyof ActiveFilters, filterValue: number, isIndexed: boolean) => {
    if (isIndexed) {
      return generateIndexedFilterPath({ [filterKey]: [filterValue] });
    }

    return null;
  };

  const getIsFilterActive = (filterKey: keyof ActiveFilters, filterValue: number) => {
    const activeFilterValue = activeFilters[filterKey];

    if (Array.isArray(activeFilterValue)) {
      return activeFilterValue.includes(filterValue);
    } else {
      return activeFilterValue === filterValue;
    }
  };

  const handleToggleFilter = (filter: Filter) => {
    if (filter.brandName) {
      if (filter.key === 'brands') {
        sendFilterBrandChoiceEvent({
          brandName: filter.brandName,
          referrer: 'popular_brands',
        });
      } else {
        setBrandPageReferrerToStorage(filter.brandName, BRAND_PAGE_REFERRER.SEO_HUB);
      }
    }

    // filter
    if (filter.key && filter.value) {
      if (catalogMatch) {
        sendFilterApplyEvent({
          namespace: catalogMatch.params.namespace,
          categorySlug: 'slug' in catalogMatch.params ? catalogMatch.params.slug : undefined,
        });
      } else if (collectionMatch) {
        sendFilterApplyEvent({
          collection: collectionMatch.params.slug,
        });
      }
    }

    // indexed filter
    if (filter.key && filter.value && filter.url && withIndexedFilters) {
      history.push(filter.url);
      // not indexed filter
    } else if (filter.key && filter.value) {
      setActiveFilter({
        [filter.key]: xor(
          activeFilters[filter.key] as number[],
          [filter.value],
        ),
      });
    // seo hub 
    } else if (filter.url) {
      window.location.href = filter.url;
    }
  };

  const characteristicsFilters = filters?.characteristics.map((filter) => {
    const filterKey = 'characteristics' as const;
    const filterValue = filter.characteristicChoice.id;
    const path = getFilterUrl(filterKey, filterValue, filter.characteristicChoice.isIndexed);
    const isActive = getIsFilterActive(filterKey, filterValue);

    return {
      title: filter.title,
      image: filter.image,
      key: filterKey,
      value: filterValue,
      url: path,
      isActive,
    };
  }) || [];

  const brandsFilters = filters?.brands.map((filter) => {
    const filterKey = 'brands' as const;
    const filterValue = filter.brand.id;
    const path = getFilterUrl(filterKey, filterValue, filter.brand.isIndexed);
    const isActive = getIsFilterActive(filterKey, filterValue);

    return {
      image: filter.image,
      key: filterKey,
      value: filterValue,
      url: path,
      isActive,
      brandName: filter.brand.name,
    };
  }) || [];

  const colorsFilters = filters?.colors.map((filter) => {
    const filterKey = 'colors' as const;
    const filterValue = Number(filter.color.id);
    const path = getFilterUrl(filterKey, filterValue, filter.color.isIndexed);
    const isActive = getIsFilterActive(filterKey, filterValue);

    return {
      title: filter.title,
      image: filter.image,
      key: filterKey,
      value: filterValue,
      url: path,
      isActive,
    };
  }) || [];

  const seoHubCategoriesAsFilters = seoHubItems?.[SeoHubBlockTypeEnum.PopularCategories]?.map((seoHubItem) => ({
    title: seoHubItem.title,
    image: seoHubItem.pictureUrl,
    url: seoHubItem.url,
  })) || [];

  const seoHubBrandsAsFilters = seoHubItems?.[SeoHubBlockTypeEnum.Brands]?.map((seoHubItem) => ({
    title: seoHubItem.title,
    url: seoHubItem.url,
    brandName: seoHubItem.title,
  })) || [];

  const seoHubFirstCustomItemsAsFilters = seoHubItems?.[SeoHubBlockTypeEnum.FirstCustomBlock]?.map((seoHubItem) => ({
    title: seoHubItem.title,
    url: seoHubItem.url,
  })) || [];

  const seoHubSecondCustomItemsAsFilters = seoHubItems?.[SeoHubBlockTypeEnum.SecondCustomBlock]?.map((seoHubItem) => ({
    title: seoHubItem.title,
    url: seoHubItem.url,
  })) || [];

  const sortedCommonFilters = [
    ...characteristicsFilters,
    ...colorsFilters,
    ...seoHubCategoriesAsFilters,
  ].sort((a, b) => {
    // first active filters
    return Number('isActive' in b && b.isActive) - Number('isActive' in a && a.isActive);
  });

  const sortedBrandFilters = brandsFilters.sort((a, b) => {
    // first active filters
    return Number('isActive' in b && b.isActive) - Number('isActive' in a && a.isActive);
  });

  return (
    <section className={className}>
      {(!!characteristicsFilters.length || !!colorsFilters.length || !!brandsFilters.length) && (
        <p className={s.title}>
          {t`Популярні фільтри`}
        </p>
      )}

      <ul className={classNames(s.list)}>
        {!!sortedCommonFilters.length && (
          <li className={s.item}>
            <CatalogThematicFilters
              filters={sortedCommonFilters}
              onToggle={handleToggleFilter}
            />
          </li>
        )}

        {!!sortedBrandFilters.length && (
          <li className={s.item}>
            <CatalogThematicFilters
              theme="brand"
              filters={sortedBrandFilters}
              onToggle={handleToggleFilter}
            />
          </li>
        )}

        {!!seoHubBrandsAsFilters.length && !sortedBrandFilters.length && (
          <li className={s.item}>
            <CatalogThematicFilters
              theme="tag"
              filters={seoHubBrandsAsFilters}
              onToggle={handleToggleFilter}
            />
          </li>
        )}

        {!!seoHubFirstCustomItemsAsFilters.length && (
          <li className={s.item}>
            <CatalogThematicFilters
              theme="tag"
              filters={seoHubFirstCustomItemsAsFilters}
              onToggle={handleToggleFilter}
            />
          </li>
        )}

        {!!seoHubSecondCustomItemsAsFilters.length && (
          <li className={s.item}>
            <CatalogThematicFilters
              theme="tag"
              filters={seoHubSecondCustomItemsAsFilters}
              onToggle={handleToggleFilter}
            />
          </li>
        )}
      </ul>
    </section>
  );
};
